import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
    Inbox, Check, X, Mail, RefreshCw, Clock, AlertCircle, Trophy, Lock, Unlock, Archive, ArchiveRestore, CalendarClock, Search, CheckSquare, Square, Trash2
} from "lucide-react";

const STATUS_TABS = [
    { k: "pending", label: "Pending", icon: Inbox },
    { k: "approved", label: "Approved", icon: Check },
    { k: "rejected", label: "Rejected", icon: X },
];

export default function SiteAdminLeagueRequests() {
    const [statusFilter, setStatusFilter] = useState("pending");
    const [includeArchived, setIncludeArchived] = useState(false);
    const [showAllModal, setShowAllModal] = useState(false);  // iter87.86
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(false);
    const [acting, setActing] = useState(null); // id currently being acted on
    const [rejectingId, setRejectingId] = useState(null);
    const [rejectReason, setRejectReason] = useState("");
    // iter31m — site-wide flag for whether the Landing form is open or closed.
    const [requestsOpen, setRequestsOpen] = useState(true);
    const [togglingOpen, setTogglingOpen] = useState(false);
    // iter72 → iter87.108 — Per-tier acceptance toggles. Premium was
    // retired in iter87.108 (Pro+ runs via Clerk, out of band). We still
    // read the `premium` key defensively in case a legacy site_settings
    // doc carries it, but the picker only exposes Free + Trial now.
    const [tierOpen, setTierOpen] = useState({ free: true, trial: true, premium: false });
    const [billingEnabled, setBillingEnabled] = useState(false);
    const [bulkBusy, setBulkBusy] = useState(false);
    // iter43f — bulk selection + search
    const [search, setSearch] = useState("");
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [bulkRejectOpen, setBulkRejectOpen] = useState(false);
    const [bulkRejectReason, setBulkRejectReason] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // iter87.91 → iter87.108 — Scope the Incoming Requests card to
            // comp/trial only. Free-tier submissions auto-approve via
            // Turnstile (iter87.80) and are surfaced read-only in Tools &
            // Audit → Free-Tier Signups. Premium requests are retired —
            // the tier Literal on the backend rejects them (iter87.108).
            const arr = await api.siteListLeagueRequests(statusFilter, includeArchived, "comp");
            setRequests(arr);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to load requests");
        }
        setLoading(false);
    }, [statusFilter, includeArchived]);

    // Fetch on mount + when filter/archived toggles change.
    // Load site-wide flag once on mount.
    useEffect(() => { load(); }, [load]);

    // Clear bulk selection + search whenever the active tab changes so users
    // never act on rows hidden from the visible list.
    useEffect(() => {
        setSelectedIds(new Set());
        setSearch("");
        setBulkRejectOpen(false);
    }, [statusFilter]);

    useEffect(() => {
        api.getSiteSettings()
            .then((r) => {
                setRequestsOpen(r?.league_requests_open !== false);
                // iter72 — Per-tier toggles
                if (r?.tier_requests_open) setTierOpen(r.tier_requests_open);
                setBillingEnabled(!!r?.billing_enabled);
            })
            .catch(() => {});
    }, []);

    const handleToggleTier = async (tierKey) => {
        const next = !tierOpen[tierKey];
        setTierOpen((prev) => ({ ...prev, [tierKey]: next })); // optimistic
        try {
            const r = await api.updateSiteSettings({ tier_requests_open: { [tierKey]: next } });
            if (r?.tier_requests_open) setTierOpen(r.tier_requests_open);
            toast.success(`${tierKey.charAt(0).toUpperCase() + tierKey.slice(1)} signups ${next ? "opened" : "paused"}`);
        } catch (err) {
            setTierOpen((prev) => ({ ...prev, [tierKey]: !next })); // revert
            toast.error(err.response?.data?.detail || "Toggle failed");
        }
    };

    const handleToggleRequestsOpen = async () => {
        const next = !requestsOpen;
        const confirmCopy = next
            ? "Open up new Rivalry requests? The Landing form will accept submissions again."
            : "Pause new Rivalry requests? Anyone who clicks \"Request Rivalry\" will see the \"requests paused\" message instead of the form.";
        if (!window.confirm(confirmCopy)) return;
        setTogglingOpen(true);
        try {
            const r = await api.updateSiteSettings({ league_requests_open: next });
            setRequestsOpen(r?.league_requests_open !== false);
            toast.success(next ? "Rivalry requests now open" : "Rivalry requests paused");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Toggle failed");
        }
        setTogglingOpen(false);
    };

    const handleApprove = async (id) => {
        if (!window.confirm("Approve this Rivalry request? The requester will get an approval email with a magic link.")) return;
        setActing(id);
        try {
            const res = await api.siteApproveLeagueRequest(id);
            const suffixNote = res.name_was_suffixed
                ? ` · auto-renamed to "${res.league_name}" (duplicate name)`
                : "";
            if (res.email_sent) {
                toast.success(`Approved · invite ${res.invite_code} · email sent${suffixNote}`);
            } else {
                toast.warning(`Approved (invite ${res.invite_code}) but email delivery failed — share the magic link manually${suffixNote}`);
            }
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Approve failed");
        }
        setActing(null);
    };

    const handleStartReject = (id) => {
        setRejectingId(id);
        setRejectReason("");
    };

    const handleConfirmReject = async () => {
        if (!rejectingId) return;
        setActing(rejectingId);
        try {
            const res = await api.siteRejectLeagueRequest(rejectingId, rejectReason);
            if (res.email_sent) {
                toast.success("Rejected · email sent");
            } else {
                toast.warning("Rejected, but email delivery failed");
            }
            setRejectingId(null);
            setRejectReason("");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Reject failed");
        }
        setActing(null);
    };

    const handleArchive = async (id) => {
        setActing(id);
        try {
            await api.siteArchiveLeagueRequest(id);
            toast.success("Archived");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Archive failed");
        }
        setActing(null);
    };

    const handleUnarchive = async (id) => {
        setActing(id);
        try {
            await api.siteUnarchiveLeagueRequest(id);
            toast.success("Restored");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Restore failed");
        }
        setActing(null);
    };

    const handleArchiveAllRejected = async () => {
        if (!window.confirm("Archive every currently-rejected Rivalry request? They'll be hidden from the default list but still available via 'Show archived'.")) return;
        setBulkBusy(true);
        try {
            const res = await api.siteArchiveAllRejected();
            toast.success(`Archived ${res.archived_count} rejected request${res.archived_count === 1 ? "" : "s"}`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk archive failed");
        }
        setBulkBusy(false);
    };

    const handleAutoArchiveOld = async () => {
        if (!window.confirm("Archive every rejected Rivalry request older than 90 days?")) return;
        setBulkBusy(true);
        try {
            const res = await api.siteAutoArchiveOld();
            toast.success(`Archived ${res.archived_count} request${res.archived_count === 1 ? "" : "s"} older than 90 days`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Auto-archive failed");
        }
        setBulkBusy(false);
    };

    // ---- iter85.2 — hard delete (rejected / archived only) ----
    const handleDelete = async (id) => {
        if (!window.confirm("Permanently delete this request? This cannot be undone.")) return;
        setActing(id);
        try {
            await api.siteDeleteLeagueRequest(id);
            toast.success("Deleted forever");
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete failed");
        }
        setActing(null);
    };

    const handlePurge = async () => {
        setBulkBusy(true);
        try {
            const dry = await api.sitePurgeLeagueRequests(true);
            if (!dry.deleted_count) {
                toast.info("Nothing to purge — no rejected or archived requests.");
                setBulkBusy(false);
                return;
            }
            if (!window.confirm(`Permanently delete ${dry.deleted_count} rejected/archived request${dry.deleted_count === 1 ? "" : "s"}? Approved and active pending requests are untouched. This cannot be undone.`)) {
                setBulkBusy(false);
                return;
            }
            const res = await api.sitePurgeLeagueRequests();
            toast.success(`Purged ${res.deleted_count} request${res.deleted_count === 1 ? "" : "s"} forever`);
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Purge failed");
        }
        setBulkBusy(false);
    };

    // ---- iter43f — search + bulk selection ----
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return requests;
        return requests.filter(r => {
            return (r.league_name || "").toLowerCase().includes(q)
                || (r.admin_email || "").toLowerCase().includes(q)
                || (r.admin_username || "").toLowerCase().includes(q)
                || (r.created_league_name || "").toLowerCase().includes(q)
                || (r.created_invite_code || "").toLowerCase().includes(q);
        });
    }, [requests, search]);

    // Only pending rows are eligible for bulk approve/reject. Archive works on any status.
    const selectableIds = useMemo(() => filtered.map(r => r.id), [filtered]);
    const bulkApproveRejectIds = useMemo(
        () => Array.from(selectedIds).filter(id => {
            const row = requests.find(r => r.id === id);
            return row && row.status === "pending";
        }),
        [selectedIds, requests]
    );
    const bulkArchiveIds = useMemo(
        () => Array.from(selectedIds).filter(id => {
            const row = requests.find(r => r.id === id);
            return row && !row.archived_at;
        }),
        [selectedIds, requests]
    );
    // iter86.14 — Delete-forever eligibility mirrors the backend guard:
    // rejected OR already archived (any status).
    const bulkDeleteForeverIds = useMemo(
        () => Array.from(selectedIds).filter(id => {
            const row = requests.find(r => r.id === id);
            return row && (row.status === "rejected" || !!row.archived_at);
        }),
        [selectedIds, requests]
    );

    const toggleRowSelected = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };
    const toggleAllSelected = () => {
        setSelectedIds(prev => {
            if (selectableIds.length > 0 && selectableIds.every(id => prev.has(id))) return new Set();
            return new Set(selectableIds);
        });
    };
    const clearSelection = () => setSelectedIds(new Set());

    const handleBulkApprove = async () => {
        if (bulkApproveRejectIds.length === 0) return;
        if (!window.confirm(`Approve ${bulkApproveRejectIds.length} pending request(s)? Each requester will receive an approval email with a magic link.`)) return;
        setBulkBusy(true);
        try {
            const res = await api.siteBulkApproveLeagueRequests(bulkApproveRejectIds);
            if (res.skipped_count > 0) {
                toast.warning(`Approved ${res.approved_count} · ${res.skipped_count} skipped (already actioned or missing)`);
            } else {
                toast.success(`Approved ${res.approved_count} request${res.approved_count === 1 ? "" : "s"}`);
            }
            clearSelection();
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk approve failed");
        }
        setBulkBusy(false);
    };

    const handleOpenBulkReject = () => {
        if (bulkApproveRejectIds.length === 0) return;
        setBulkRejectReason("");
        setBulkRejectOpen(true);
    };

    const handleConfirmBulkReject = async () => {
        if (bulkApproveRejectIds.length === 0) return;
        setBulkBusy(true);
        try {
            const res = await api.siteBulkRejectLeagueRequests(bulkApproveRejectIds, bulkRejectReason);
            if (res.skipped_count > 0) {
                toast.warning(`Rejected ${res.rejected_count} · ${res.skipped_count} skipped (already actioned or missing)`);
            } else {
                toast.success(`Rejected ${res.rejected_count} request${res.rejected_count === 1 ? "" : "s"}`);
            }
            setBulkRejectOpen(false);
            setBulkRejectReason("");
            clearSelection();
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk reject failed");
        }
        setBulkBusy(false);
    };

    const handleBulkArchive = async () => {
        if (bulkArchiveIds.length === 0) return;
        if (!window.confirm(`Archive ${bulkArchiveIds.length} request(s)? They'll be hidden from the default list but still available via "Show archived".`)) return;
        setBulkBusy(true);
        try {
            const res = await api.siteBulkArchiveLeagueRequests(bulkArchiveIds);
            toast.success(`Archived ${res.archived_count} request${res.archived_count === 1 ? "" : "s"}`);
            clearSelection();
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Bulk archive failed");
        }
        setBulkBusy(false);
    };

    // iter86.14 — Permanent delete for selected rejected/archived requests.
    const handleBulkDeleteForever = async () => {
        if (bulkDeleteForeverIds.length === 0) return;
        if (!window.confirm(`Delete ${bulkDeleteForeverIds.length} request(s) FOREVER? Only rejected or archived requests are deleted — this cannot be undone.`)) return;
        setBulkBusy(true);
        try {
            const res = await api.siteBulkDeleteLeagueRequestsForever(bulkDeleteForeverIds);
            if (res.skipped_count > 0) {
                toast.warning(`Deleted ${res.deleted_count} · ${res.skipped_count} skipped (not rejected/archived)`);
            } else {
                toast.success(`Deleted ${res.deleted_count} request${res.deleted_count === 1 ? "" : "s"} forever`);
            }
            clearSelection();
            await load();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Delete forever failed");
        }
        setBulkBusy(false);
    };

    const allFilteredSelected = selectableIds.length > 0 && selectableIds.every(id => selectedIds.has(id));

    return (
        <div className="space-y-4" data-testid="site-requests-tab">
            {/* iter87.80 — Landing gate + per-tier acceptance moved to
                the Rivalry Subscriptions tab. This tab now focuses on
                the pending-request queue only. */}
            {/* iter87.91 — Scoped to comp/trial approvals only. Free-tier
                requests auto-approve via Turnstile and now live under
                Tools & Audit → Free-Tier Signups. */}
            <div
                className="rounded border border-[#222] bg-[#111]/40 px-3 py-2 text-[11px] text-zinc-400"
                data-testid="req-free-tier-audit-pointer"
            >
                Only comp / trial submissions surface here. Free-tier signups
                auto-approve via Turnstile — see{" "}
                <span className="text-cyber">Tools & Audit → Free-Tier Signups</span>{" "}
                for the full history.
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex flex-wrap gap-2">
                    {STATUS_TABS.map(t => (
                        <button
                            key={t.k}
                            data-testid={`req-filter-${t.k}-btn`}
                            onClick={() => setStatusFilter(t.k)}
                            className={`brutal-btn text-xs py-2 px-3 ${statusFilter === t.k ? "bg-cyber text-black" : "bg-transparent text-white border-[#333] shadow-none"}`}
                        >
                            <t.icon className="w-3.5 h-3.5" /> {t.label}
                        </button>
                    ))}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 cursor-pointer select-none">
                        <input
                            type="checkbox"
                            data-testid="req-show-archived-toggle"
                            checked={includeArchived}
                            onChange={(e) => setIncludeArchived(e.target.checked)}
                            className="w-3.5 h-3.5 accent-cyber cursor-pointer"
                        />
                        Show archived
                    </label>
                    <button onClick={load} className="brutal-btn-ghost text-xs py-2" title="Refresh">
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                    </button>
                </div>
            </div>

            {/* iter43f — Search box (works across all tabs) */}
            <div className="brutal-card p-3 flex flex-wrap items-center gap-3" data-testid="req-search-bar">
                <div className="flex-1 min-w-[200px] relative">
                    <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                    <input
                        data-testid="req-search-input"
                        type="text"
                        placeholder="Search by Rivalry name, admin email, username, or invite code…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="brutal-input w-full pl-9 text-sm"
                    />
                </div>
                <p className="text-[11px] text-zinc-500" data-testid="req-result-count">
                    {filtered.length} of {requests.length}
                </p>
                {filtered.length > 0 && (
                    <button
                        data-testid="req-select-all-btn"
                        onClick={toggleAllSelected}
                        className="brutal-btn-ghost text-xs py-2"
                    >
                        {allFilteredSelected
                            ? <><CheckSquare className="w-3.5 h-3.5 text-cyber" /> Deselect all</>
                            : <><Square className="w-3.5 h-3.5" /> Select all</>}
                    </button>
                )}
            </div>

            {/* iter43f — Bulk action bar (sticky), only shows when rows selected */}
            {selectedIds.size > 0 && (
                <div
                    className="brutal-card p-3 flex flex-wrap items-center justify-between gap-3 sticky top-[88px] z-20 bg-cyber/10 border-cyber"
                    data-testid="req-bulk-action-bar"
                >
                    <p className="text-sm font-bold">
                        <span className="text-cyber">{selectedIds.size}</span> request{selectedIds.size === 1 ? "" : "s"} selected
                    </p>
                    <div className="flex gap-2 flex-wrap">
                        <button
                            data-testid="req-clear-selection-btn"
                            onClick={clearSelection}
                            className="brutal-btn-ghost text-xs py-2"
                        >
                            <X className="w-3.5 h-3.5" /> Clear
                        </button>
                        {bulkApproveRejectIds.length > 0 && (
                            <>
                                <button
                                    data-testid="req-bulk-approve-btn"
                                    onClick={handleBulkApprove}
                                    disabled={bulkBusy}
                                    className="brutal-btn text-xs py-2 bg-cyber text-black border-black shadow-brutalYellow disabled:opacity-60"
                                >
                                    <Check className="w-3.5 h-3.5" /> Approve {bulkApproveRejectIds.length}
                                </button>
                                <button
                                    data-testid="req-bulk-reject-btn"
                                    onClick={handleOpenBulkReject}
                                    disabled={bulkBusy}
                                    className="brutal-btn text-xs py-2 bg-heat text-white border-black shadow-brutal disabled:opacity-60"
                                >
                                    <X className="w-3.5 h-3.5" /> Reject {bulkApproveRejectIds.length}
                                </button>
                            </>
                        )}
                        {bulkArchiveIds.length > 0 && (
                            <button
                                data-testid="req-bulk-archive-btn"
                                onClick={handleBulkArchive}
                                disabled={bulkBusy}
                                className="brutal-btn text-xs py-2 bg-zinc-800 text-white border-black shadow-brutal disabled:opacity-60"
                            >
                                <Archive className="w-3.5 h-3.5" /> Archive {bulkArchiveIds.length}
                            </button>
                        )}
                        {bulkDeleteForeverIds.length > 0 && (
                            <button
                                data-testid="req-bulk-delete-forever-btn"
                                onClick={handleBulkDeleteForever}
                                disabled={bulkBusy}
                                title="Permanently delete selected rejected/archived requests. Cannot be undone."
                                className="brutal-btn text-xs py-2 bg-red-700 text-white border-black shadow-brutal disabled:opacity-60"
                            >
                                <Trash2 className="w-3.5 h-3.5" /> Delete forever {bulkDeleteForeverIds.length}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* iter43f — Bulk reject reason form */}
            {bulkRejectOpen && (
                <div className="brutal-card p-4 space-y-3 border-heat" data-testid="req-bulk-reject-form">
                    <p className="font-bold text-sm">
                        Reject {bulkApproveRejectIds.length} request{bulkApproveRejectIds.length === 1 ? "" : "s"}
                    </p>
                    <label className="block text-[10px] uppercase tracking-widest text-zinc-500">
                        Reason (optional, included in every email)
                    </label>
                    <textarea
                        data-testid="req-bulk-reject-reason-input"
                        value={bulkRejectReason}
                        onChange={(e) => setBulkRejectReason(e.target.value)}
                        rows={3}
                        placeholder="e.g. We're not accepting new Rivalries until the WC group draw is finalized."
                        className="brutal-input w-full text-sm resize-y"
                    />
                    <div className="flex gap-2">
                        <button
                            data-testid="req-bulk-reject-confirm-btn"
                            onClick={handleConfirmBulkReject}
                            disabled={bulkBusy}
                            className="brutal-btn text-xs py-2 bg-heat text-white border-black shadow-brutal disabled:opacity-60"
                        >
                            <Mail className="w-3.5 h-3.5" /> Send {bulkApproveRejectIds.length} rejection{bulkApproveRejectIds.length === 1 ? "" : "s"}
                        </button>
                        <button
                            onClick={() => { setBulkRejectOpen(false); setBulkRejectReason(""); }}
                            className="brutal-btn-ghost text-xs py-2"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Bulk archive actions — only meaningful for the rejected tab */}
            {statusFilter === "rejected" && (
                <div className="brutal-card-light p-3 flex flex-wrap items-center justify-between gap-3 border-blaze/30" data-testid="req-bulk-archive-bar">
                    <p className="text-[11px] text-zinc-400">
                        Clean up in bulk — archive hides rows from the default list; purge deletes rejected + archived rows forever (approved and active pending are never touched).
                    </p>
                    <div className="flex gap-2 flex-wrap">
                        <button
                            data-testid="req-archive-all-rejected-btn"
                            onClick={handleArchiveAllRejected}
                            disabled={bulkBusy}
                            className="brutal-btn text-xs py-2 bg-zinc-800 text-white border-black shadow-brutal disabled:opacity-60"
                        >
                            <Archive className="w-3.5 h-3.5" /> Archive all rejected
                        </button>
                        <button
                            data-testid="req-auto-archive-old-btn"
                            onClick={handleAutoArchiveOld}
                            disabled={bulkBusy}
                            className="brutal-btn-ghost text-xs py-2 disabled:opacity-60"
                        >
                            <CalendarClock className="w-3.5 h-3.5" /> Archive 90+ days old
                        </button>
                        <button
                            data-testid="req-purge-btn"
                            onClick={handlePurge}
                            disabled={bulkBusy}
                            className="brutal-btn text-xs py-2 bg-heat text-white border-black shadow-brutal disabled:opacity-60"
                            title="Permanently delete all rejected and archived requests"
                        >
                            <Trash2 className="w-3.5 h-3.5" /> Purge rejected & archived
                        </button>
                    </div>
                </div>
            )}

            {filtered.length === 0 && !loading && (
                <div className="brutal-card p-8 text-center" data-testid="req-empty-state">
                    <Inbox className="w-10 h-10 mx-auto text-zinc-700 mb-2" />
                    <p className="text-zinc-500 text-sm">
                        {search.trim() ? `No ${statusFilter} requests match "${search.trim()}".` : `No ${statusFilter} requests.`}
                    </p>
                </div>
            )}

            {/* iter87.86 — Mobile-friendly cap at 20 rows with "View all N" escape hatch. */}
            <div className="space-y-3" data-testid="req-list">
                {filtered.slice(0, 20).map(r => {
                    const selected = selectedIds.has(r.id);
                    return (
                    <div
                        key={r.id}
                        className={`brutal-card p-4 space-y-3 ${selected ? "border-cyber shadow-brutalYellow" : ""}`}
                        data-testid={`req-row-${r.id}`}
                    >
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div className="flex items-start gap-3 min-w-0 flex-1">
                                <button
                                    type="button"
                                    data-testid={`req-select-${r.id}-checkbox`}
                                    onClick={() => toggleRowSelected(r.id)}
                                    className="mt-1 shrink-0"
                                    aria-label={selected ? "Deselect" : "Select"}
                                    aria-pressed={selected}
                                >
                                    {selected
                                        ? <CheckSquare className="w-5 h-5 text-cyber" strokeWidth={2.5} />
                                        : <Square className="w-5 h-5 text-zinc-500 hover:text-zinc-300" strokeWidth={2} />}
                                </button>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Trophy className="w-4 h-4 text-cyber shrink-0" />
                                        <p className="font-display font-extrabold text-lg truncate">{r.league_name}</p>
                                        <StatusPill status={r.status} />
                                        {/* iter71 — Tier requested by the user, shown next to the
                                            status pill. Approved rows show the FINAL tier the
                                            admin granted (may differ if the admin overrode). */}
                                        {(r.requested_tier || r.approved_tier) && (
                                            <RequestTierPill
                                                requested={r.requested_tier}
                                                approved={r.approved_tier}
                                                status={r.status}
                                            />
                                        )}
                                        {r.archived_at && (
                                            <span className="pill bg-zinc-800/60 border-zinc-600 text-zinc-400 border-2 text-[9px] py-0.5" data-testid={`req-archived-pill-${r.id}`}>
                                                <Archive className="w-2.5 h-2.5 inline mr-1" />Archived
                                            </span>
                                        )}
                                    </div>
                                    <div className="mt-1 text-xs text-zinc-400 space-y-1">
                                        <p><span className="text-zinc-500">admin:</span> <span className="text-white">{r.admin_username}</span> · <span className="text-cyber">{r.admin_email}</span></p>
                                        <p><span className="text-zinc-500">teams per crewmate:</span> {r.teams_per_participant} · <Clock className="w-3 h-3 inline mb-0.5" /> {new Date(r.created_at).toLocaleString()}</p>
                                        {/* iter71 — Trial code surfaced when present */}
                                        {r.trial_code && (
                                            <p data-testid={`req-trial-code-${r.id}`}>
                                                <span className="text-zinc-500">trial code:</span>{" "}
                                                <span className="font-mono text-volt font-bold">{r.trial_code}</span>
                                                {r.approved_trial_code && r.approved_trial_code !== r.trial_code && (
                                                    <> · <span className="text-zinc-500">approved with:</span>{" "}
                                                        <span className="font-mono text-cyber font-bold">{r.approved_trial_code}</span></>
                                                )}
                                            </p>
                                        )}
                                        {r.status === "approved" && r.created_invite_code && (
                                            <>
                                                <p><span className="text-zinc-500">approved:</span> {new Date(r.approved_at).toLocaleString()} · invite <span className="font-mono text-cyber font-bold">{r.created_invite_code}</span> · email {r.email_sent ? "✓ sent" : "✗ failed"}</p>
                                                {r.created_league_name && r.created_league_name !== r.league_name && (
                                                    <p className="text-volt">
                                                        <span className="text-zinc-500">auto-renamed to:</span> <span className="text-volt font-bold">{r.created_league_name}</span> (duplicate name)
                                                    </p>
                                                )}
                                            </>
                                        )}
                                        {r.status === "rejected" && (
                                            <>
                                                <p><span className="text-zinc-500">rejected:</span> {new Date(r.rejected_at).toLocaleString()} · email {r.email_sent ? "✓ sent" : "✗ failed"}</p>
                                                {r.rejection_reason && (
                                                    <p className="border-l-2 border-heat pl-2 mt-2 text-zinc-300 italic">{r.rejection_reason}</p>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {r.status === "pending" && (
                                    <>
                                        <button
                                            data-testid={`req-approve-${r.id}-btn`}
                                            onClick={() => handleApprove(r.id)}
                                            disabled={acting === r.id}
                                            className="brutal-btn text-xs py-2 bg-cyber text-black border-black shadow-brutalYellow disabled:opacity-60"
                                        >
                                            <Check className="w-3.5 h-3.5" /> Approve
                                        </button>
                                        <button
                                            data-testid={`req-reject-${r.id}-btn`}
                                            onClick={() => handleStartReject(r.id)}
                                            disabled={acting === r.id}
                                            className="brutal-btn text-xs py-2 bg-heat text-white border-black shadow-brutal disabled:opacity-60"
                                        >
                                            <X className="w-3.5 h-3.5" /> Reject
                                        </button>
                                    </>
                                )}
                                {/* Archive / Unarchive controls — available on any status */}
                                {r.archived_at ? (
                                    <button
                                        data-testid={`req-unarchive-${r.id}-btn`}
                                        onClick={() => handleUnarchive(r.id)}
                                        disabled={acting === r.id}
                                        className="brutal-btn-ghost text-xs py-2 disabled:opacity-60"
                                        title="Restore to the active list"
                                    >
                                        <ArchiveRestore className="w-3.5 h-3.5" /> Unarchive
                                    </button>
                                ) : (
                                    <button
                                        data-testid={`req-archive-${r.id}-btn`}
                                        onClick={() => handleArchive(r.id)}
                                        disabled={acting === r.id}
                                        className="brutal-btn-ghost text-xs py-2 disabled:opacity-60"
                                        title="Hide this request from the default list"
                                    >
                                        <Archive className="w-3.5 h-3.5" /> Archive
                                    </button>
                                )}
                                {(r.status === "rejected" || (r.archived_at && r.status !== "approved")) && (
                                    <button
                                        data-testid={`req-delete-${r.id}-btn`}
                                        onClick={() => handleDelete(r.id)}
                                        disabled={acting === r.id}
                                        className="brutal-btn-ghost text-xs py-2 text-red-400 disabled:opacity-60"
                                        title="Permanently delete this request"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" /> Delete forever
                                    </button>
                                )}
                            </div>
                        </div>

                        {rejectingId === r.id && (
                            <div className="border-t-2 border-[#222] pt-3 space-y-2" data-testid={`req-reject-form-${r.id}`}>
                                <label className="block text-[10px] uppercase tracking-widest text-zinc-500">
                                    Reason (optional, will be included in the email)
                                </label>
                                <textarea
                                    data-testid="req-reject-reason-input"
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    rows={3}
                                    placeholder="e.g. We're not accepting new Rivalries until the WC group draw is finalized."
                                    className="brutal-input w-full text-sm resize-y"
                                />
                                <div className="flex gap-2">
                                    <button
                                        data-testid="req-reject-confirm-btn"
                                        onClick={handleConfirmReject}
                                        disabled={acting === r.id}
                                        className="brutal-btn text-xs py-2 bg-heat text-white border-black shadow-brutal disabled:opacity-60"
                                    >
                                        <Mail className="w-3.5 h-3.5" /> Send rejection
                                    </button>
                                    <button
                                        onClick={() => { setRejectingId(null); setRejectReason(""); }}
                                        className="brutal-btn-ghost text-xs py-2"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    );
                })}
            </div>

            {/* iter87.86 — "View all" escape hatch when the filtered list exceeds 20 rows. */}
            {filtered.length > 20 && (
                <button
                    type="button"
                    onClick={() => setShowAllModal(true)}
                    className="brutal-btn-ghost w-full text-xs py-2.5 mt-1"
                    data-testid="req-view-all-btn"
                >
                    View all {filtered.length} {statusFilter} requests →
                </button>
            )}
            {showAllModal && (
                <div
                    className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
                    onClick={() => setShowAllModal(false)}
                    data-testid="req-view-all-modal"
                >
                    <div
                        className="brutal-card max-w-3xl w-full max-h-[85dvh] overflow-hidden flex flex-col"
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="p-4 border-b-2 border-zinc-800 flex items-center gap-2">
                            <Inbox className="w-4 h-4 text-cyber" />
                            <h2 className="font-display font-black text-sm uppercase tracking-wide">
                                All {filtered.length} {statusFilter} requests
                            </h2>
                            <button
                                type="button"
                                onClick={() => setShowAllModal(false)}
                                data-testid="req-view-all-modal-close"
                                className="brutal-btn-ghost text-xs py-1 ml-auto"
                            >
                                Close
                            </button>
                        </div>
                        <div className="p-4 space-y-2 flex-1 overflow-y-auto text-xs">
                            {filtered.map(r => (
                                <div
                                    key={r.id}
                                    className="brutal-card-light p-3 flex flex-wrap items-center justify-between gap-2"
                                    data-testid={`req-modal-row-${r.id}`}
                                >
                                    <div className="min-w-0 flex-1">
                                        <p className="font-bold text-sm truncate">{r.league_name || "—"}</p>
                                        <p className="text-[11px] text-zinc-500 truncate">
                                            {r.admin_email || "—"} · {r.requested_tier || "—"} · {r.status || "—"}
                                        </p>
                                    </div>
                                    <span className="text-[10px] text-zinc-500 font-mono">
                                        {r.created_at?.slice(0, 10) || ""}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            <div className="text-[11px] text-zinc-500 flex items-start gap-2 px-1">
                <AlertCircle className="w-3 h-3 text-zinc-500 shrink-0 mt-0.5" />
                <span>Approval auto-creates the Rivalry + admin participant, generates a 7-day magic link, and emails the requester. Rejection sends a polite email with your optional reason.</span>
            </div>
        </div>
    );
}

function StatusPill({ status }) {
    const map = {
        pending: { bg: "bg-volt/20 border-volt text-volt", label: "Pending" },
        approved: { bg: "bg-cyber/20 border-cyber text-cyber", label: "Approved" },
        rejected: { bg: "bg-heat/20 border-heat text-heat", label: "Rejected" },
    };
    const s = map[status] || map.pending;
    return (
        <span className={`pill ${s.bg} border-2 text-[9px] py-0.5`}>{s.label}</span>
    );
}



// iter71 → iter87.108 — Tier pill for request rows. Shows the FINAL approved
// tier for approved requests (after admin override) and the REQUESTED tier
// for pending/rejected ones. Compact — sits alongside the StatusPill.
// iter87.108 — "premium" is a retired tier (no new requests can be submitted
// as premium; direct per-league Stripe billing was never wired). We keep
// the map entry so legacy rows render sensibly, and relabel to "Pro+ (legacy)"
// so the vocabulary matches the live Pro+ (user-level Clerk) offering.
function RequestTierPill({ requested, approved, status }) {
    const effective = status === "approved" && approved ? approved : requested;
    if (!effective) return null;
    const map = {
        free: { cls: "bg-zinc-800/60 border-zinc-500 text-zinc-300", label: "Free" },
        trial: { cls: "bg-volt/20 border-volt text-volt", label: "Trial" },
        premium: { cls: "bg-neon/20 border-neon text-neon", label: "Pro+ (legacy)" },
    };
    const m = map[effective] || map.free;
    const overrode = status === "approved" && approved && requested && approved !== requested;
    return (
        <span
            className={`pill border-2 text-[9px] py-0.5 ${m.cls}`}
            data-testid={`req-tier-pill-${effective}`}
            title={
                effective === "premium"
                    ? "Legacy 'Premium' request — this tier was retired in iter87.108. Pro+ is now a user-level Clerk subscription, not a per-league grant."
                    : overrode
                        ? `Requested as ${requested}, granted as ${approved}`
                        : `Tier: ${effective}`
            }
        >
            {m.label}
            {overrode ? "*" : ""}
        </span>
    );
}
